import React, { useCallback, useEffect, useRef } from 'react';
import { find, map } from 'lodash';
import { usePressEscape } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { useBooking, useGetViewObject, usePhases } from '../../../hooks';
import { useMilestones } from '../../../hooks/useMilestones';
import { useProjectDates } from '../../../hooks/useProjectDates';
import { companyApprovers } from '../../../../../actions/companyActions';
import { getCategoryTemplates } from '../../../../../actions/categoryTemplateActions';
import { getCategoryGroups } from '../../../../../actions/categoryGroupActions';
import { initContextMenuScheduleForm } from '../../../../../actions/schedulerActions';
import { SchedulerEditBookingMenu, ApprovalContextMenu } from '@hub-mono/app-modules';
import { useEditBookingTitleModal } from '../../dropdowns/booking/partials/editBookingTitle';
import { selectCurrentSelectedBooking, selectSchedulerMode } from '../../../../../selectors/scheduler';
import {
    showBulkMoveModal,
    showDeleteBookingConfirmationModal,
    showScheduleResourceModal
} from '../../../../../actions/modalActions';
import { PARENT } from '../../../enums/viewModeEnum';
import { WAITING_FOR_APPROVAL} from '../../../../../enums/bookingTypeEnum';

const EditBooking = props => {
    const { top, left, schedulerRef, close } = props;

    const mountedRef = useRef(false);
    usePressEscape(close);

    useEffect(() => {
        const refTimeout = setTimeout(() => {
            mountedRef.current = true;
        }, 0);

        return () => {
            clearTimeout(refTimeout);
            mountedRef.current = false;
        };
    }, []);

    return <NewEditMenu onClose={close} schedulerRef={schedulerRef} position={{left, top}} isOpen={true} />;
};

export default EditBooking;

export const NewEditMenu = ({ onClose, schedulerRef, position, isOpen, anchorEl }) => {
    const dispatch = useDispatch();
    const currentSelectedBooking = useSelector(selectCurrentSelectedBooking);

    const {
        pasteBooking,
        requestProject,
        copyBooking,
        cutBooking,
        duplicateBooking,
        updateBooking,
        deleteBookingFromModal,
        splitBooking,
        deleteBooking,
        bulkCreateBookings,
    } = useBooking(currentSelectedBooking, onClose);
    const { createMilestone } = useMilestones(schedulerRef, onClose);
    const { createPhase } = usePhases(schedulerRef, onClose);
    const { updateProjectDates } = useProjectDates(schedulerRef, onClose);
    const mode = useSelector(selectSchedulerMode);
    const viewObject = useGetViewObject();

    const editBookingTitle = useEditBookingTitleModal({
        onClose: onClose,
        currentSelectedBooking,
        updateBooking,
        mode,
        viewObject,
    });

    const bulkMoveModal = useCallback(() => {
        onClose && onClose();
        dispatch(showBulkMoveModal(schedulerRef, viewObject));
    }, [dispatch, onClose, schedulerRef, viewObject]);

    const bookingModal = useCallback(
        activeTab => {
            onClose && onClose();
            dispatch(
                showScheduleResourceModal({
                    onSubmit: updateBooking,
                    activeTab: activeTab,
                    bookingId: currentSelectedBooking.id,
                    onDelete: deleteBookingFromModal,
                })
            );
        },
        [onClose, dispatch, updateBooking, currentSelectedBooking.id, deleteBookingFromModal]
    );

    const dpResources = useSelector(state => state.scheduler.dpResources);
    const assignToAllInGroup = useCallback(() => {
        let resourceIds = map(dpResources, resource => resource._id);
        if (viewObject.isProjectView && mode === PARENT.value) {
            resourceIds =
                find(dpResources, resource => resource._id === currentSelectedBooking.project._id)?.resources || [];
        }
        bulkCreateBookings(currentSelectedBooking.id, {
            resourceIds,
        });
    }, [bulkCreateBookings, currentSelectedBooking, dpResources, mode, viewObject.isProjectView]);

    useEffect(() => {
        dispatch(companyApprovers.request());
        dispatch(getCategoryTemplates.request());
        dispatch(getCategoryGroups.request());
        dispatch(initContextMenuScheduleForm.request({edit: true}));
    }, [dispatch]);

    const handleDelete = useCallback(() => {
        dispatch(showDeleteBookingConfirmationModal(deleteBooking));
    }, [deleteBooking, dispatch]);

    if (WAITING_FOR_APPROVAL.value === currentSelectedBooking.type) {
        return (
            <ApprovalContextMenu
                anchorEl={anchorEl}
                isOpen={isOpen}
                position={position}
                viewObject={viewObject}
                onClose={onClose}
                onUpdateBooking={updateBooking}
                onDeleteBooking={deleteBooking}
            />
        )
    }

    return (
        <SchedulerEditBookingMenu
            viewObject={viewObject}
            anchorEl={anchorEl}
            isOpen={isOpen}
            position={position}
            onPasteBooking={pasteBooking}
            onCopyBooking={copyBooking}
            onCutBooking={cutBooking}
            onDuplicateBooking={duplicateBooking}
            onRequestVacation={requestProject}
            onClose={onClose}
            onCreateMilestone={createMilestone}
            onCreatePhase={createPhase}
            onUpdateProjectDates={updateProjectDates}
            onEditBookingTitle={editBookingTitle}
            onBulkMove={bulkMoveModal}
            showBookingModal={bookingModal}
            onSplitBooking={splitBooking}
            onDeleteBooking={handleDelete}
            onUpdateBooking={updateBooking}
            onAssignToAll={assignToAllInGroup}
        />
    );
};
